import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { BadgeCheck, Eye, Heart, UserPlus, UserMinus, Video } from 'lucide-react';
import { UserAvatar } from '@/components/ui';
import { http } from '@/network/http';

interface PublicProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  handle: string | null;
  bio: string | null;
  avatarUrl: string | null;
  isCreator: boolean;
  creatorVerified: boolean;
  followerCount: number;
  followingCount: number;
  videoCount: number;
  isFollowing: boolean;
  isSelf: boolean;
  videos: PublicVideo[];
}

interface PublicVideo {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  viewCount: number;
  likeCount: number;
}

export function PublicProfilePage() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery<PublicProfile>({
    queryKey: ['public-profile', userId],
    queryFn: async () => {
      const res = await http.get(`/users/${userId}/public-profile`);
      return res.data;
    },
    enabled: !!userId,
  });

  const followMutation = useMutation({
    mutationFn: async () => {
      if (profile?.isFollowing) {
        await http.delete(`/follows/${userId}/follow`);
      } else {
        await http.post(`/follows/${userId}/follow`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-profile', userId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-500">{t('common.loading')}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-500">{t('common.notFound')}</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6 px-4 sm:px-6 md:px-8 max-w-4xl mx-auto"
    >
      {/* Profile header */}
      <div
        className="rounded-2xl p-6 sm:p-8"
        style={{
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(148,163,184,0.2)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
          <UserAvatar
            src={profile.avatarUrl}
            name={`${profile.firstName || ''} ${profile.lastName || ''}`.trim()}
            size="xl"
          />

          <div className="flex-1 text-center sm:text-start">
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">
                {`${profile.firstName || ''} ${profile.lastName || ''}`.trim()}
              </h1>
              {profile.creatorVerified && (
                <BadgeCheck className="h-5 w-5 text-blue-500 flex-shrink-0" />
              )}
            </div>

            {profile.handle && <p className="text-sm text-slate-500 mt-0.5">@{profile.handle}</p>}

            {profile.bio && (
              <p className="text-sm text-slate-700 mt-3 max-w-lg">{profile.bio}</p>
            )}

            {/* Stats */}
            <div className="flex items-center justify-center sm:justify-start gap-5 mt-4">
              <div className="text-center">
                <span className="text-lg font-bold text-slate-900">
                  {profile.followerCount.toLocaleString()}
                </span>
                <p className="text-xs text-slate-500">{t('profile.followers')}</p>
              </div>
              <div className="text-center">
                <span className="text-lg font-bold text-slate-900">
                  {profile.followingCount.toLocaleString()}
                </span>
                <p className="text-xs text-slate-500">{t('profile.following')}</p>
              </div>
            </div>

            {/* Follow button */}
            {!profile.isSelf && <button
              onClick={() => followMutation.mutate()}
              disabled={followMutation.isPending}
              className={`mt-4 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                profile.isFollowing
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  : 'bg-indigo-500 text-white hover:bg-indigo-600'
              }`}
            >
              {profile.isFollowing ? (
                <>
                  <UserMinus className="h-4 w-4" />
                  {t('profile.unfollow')}
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4" />
                  {t('profile.follow')}
                </>
              )}
            </button>}
          </div>
        </div>
      </div>

      {/* Videos section */}
      <div>
        <h2 className="text-base font-semibold text-slate-800 mb-3">
          {t('profile.videos')}
        </h2>

        {profile.videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Video className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">{t('profile.noVideos')}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {profile.videos.map((video) => (
              <motion.div
                key={video.id}
                whileHover={{ y: -2 }}
                className="rounded-xl overflow-hidden transition-shadow hover:shadow-lg"
                style={{
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(148,163,184,0.2)',
                }}
              >
                {video.thumbnailUrl ? (
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="aspect-video w-full object-cover"
                  />
                ) : (
                  <div className="aspect-video w-full bg-slate-100 flex items-center justify-center">
                    <Video className="h-8 w-8 text-slate-300" />
                  </div>
                )}
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-slate-900 truncate">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5" />
                      {video.viewCount.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="h-3.5 w-3.5" />
                      {video.likeCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
